import { useRef } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

export const ContactFooter = () => {
    const wrapRef = useRef(null);
    const x = useMotionValue(0);
    const y = useMotionValue(0);
    const sx = useSpring(x, { stiffness: 120, damping: 16, mass: 0.4 });
    const sy = useSpring(y, { stiffness: 120, damping: 16, mass: 0.4 });

    const onMove = (e) => {
        const r = wrapRef.current.getBoundingClientRect();
        x.set((e.clientX - (r.left + r.width / 2)) * 0.12);
        y.set((e.clientY - (r.top + r.height / 2)) * 0.2);
    };
    const onLeave = () => {
        x.set(0);
        y.set(0);
    };

    return (
        <footer
            data-testid="contact-footer"
            className="relative overflow-hidden bg-plum text-ivory"
        >
            <div className="px-6 pb-14 pt-28 md:px-12 md:pt-44">
                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9, ease: EASE }}
                    className="label-xs mb-12 flex items-center gap-4 text-ivory/50 md:mb-20"
                    data-testid="contact-kicker"
                >
                    <span className="h-px w-10 bg-ivory/30" aria-hidden="true" />
                    Need a hand in the meantime?
                </motion.p>

                <div
                    ref={wrapRef}
                    onMouseMove={onMove}
                    onMouseLeave={onLeave}
                    className="inline-block"
                >
                    <motion.a
                        href="mailto:help@rentoui.com"
                        data-testid="email-cta-button"
                        style={{ x: sx, y: sy }}
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 1.1, ease: EASE }}
                        className="group flex items-center gap-4 font-display text-[9.5vw] font-medium italic leading-none text-lavender transition-colors duration-500 hover:text-ivory md:text-[6.5rem]"
                    >
                        help@rentoui.com
                        <ArrowUpRight
                            className="h-[0.6em] w-[0.6em] shrink-0 transition-transform duration-500 ease-out group-hover:-translate-y-2 group-hover:translate-x-2"
                            strokeWidth={1.5}
                            aria-hidden="true"
                        />
                    </motion.a>
                </div>

                <motion.p
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1, delay: 0.3 }}
                    className="mt-10 max-w-md text-sm leading-relaxed text-ivory/50 md:text-base"
                    data-testid="contact-note"
                >
                    For anything at all — an active rental, a listing, a
                    question — write to us and a real human will reply.
                </motion.p>
            </div>

            <div
                className="flex flex-col items-start justify-between gap-10 border-t border-ivory/10 px-6 py-12 md:flex-row md:items-end md:px-12"
                data-testid="footer-bar"
            >
                <img
                    src="/assets/logo-light.png"
                    alt="RentOui"
                    className="h-10 w-auto md:h-14"
                    data-testid="footer-logo"
                />
                <div className="flex flex-col gap-3 md:items-end">
                    <p
                        className="label-xs text-ivory/40"
                        data-testid="footer-tagline"
                    >
                        Peer-to-peer designer handbag rental
                    </p>
                    <p
                        className="label-xs text-ivory/30"
                        data-testid="footer-copyright"
                    >
                        © 2026 RentOui — Be back soon
                    </p>
                </div>
            </div>
        </footer>
    );
};
